import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Calendar, Clock, ChevronRight, Search, Tag } from 'lucide-react';
import HuscleosHeader from '../../components/huscleos/HuscleosHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';
const C = { accent: '#FF2E00', dark: '#0F0F0F', text: '#1A1A1A', muted: '#6B7280', light: '#9CA3AF', border: '#E5E7EB' };

const CATEGORIES = ['All', 'Operations', 'Marketing', 'Sales', 'Retention', 'Funnels', 'Tech', 'Culture'];

/* ── Blog List Page ────────────────────────────────────────── */
export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/blog?limit=50`).then(r => r.json()).then(d => { setPosts(d.posts || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = posts.filter(p => {
    if (category !== 'All' && p.category !== category) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase()) && !(p.excerpt || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif', background: '#FAFAFA' }}>
      <Helmet>
        <title>The HuscleOS Blog — Gym Software, Marketing & Retention for Independent Gyms</title>
        <meta name="description" content="Operator-level playbooks for independent gym owners: CRM workflows, lead acquisition, follow-up automation, retention math, sales funnels, and the real cost of software fragmentation." />
        <meta name="keywords" content="gym blog, gym software blog, gym marketing, gym retention, gym sales funnels, gym CRM, independent gym owner resources, gym lead generation" />
        <link rel="canonical" href="https://huscleos.com/blog" />
        <meta property="og:title" content="The HuscleOS Blog — For Independent Gym Operators" />
        <meta property="og:description" content="Operator-level playbooks for independent gym owners. CRM, lead-gen, retention, and growth frameworks that actually work." />
        <meta property="og:url" content="https://huscleos.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>
      <HuscleosHeader variant="solid" />
      <div className="h-16" />

      {/* Hero */}
      <section className="py-14 sm:py-20 text-center" style={{ background: C.dark }}>
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: C.accent }}>THE HUSCLEOS BLOG</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-3" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>
            RUN IT LIKE YOU <span style={{ color: C.accent }}>MEAN IT</span>.
          </h1>
          <p className="text-sm text-gray-400 max-w-lg mx-auto">
            Everything you need to run a fitness business that grows — funnels, conversions, marketing, sales, operations, retention, and the systems that tie it all together.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b bg-white" style={{ borderColor: C.border }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 w-full sm:max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..."
                className="w-full pl-9 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:border-[#FF2E00]" style={{ borderColor: C.border }} data-testid="blog-search" />
            </div>
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`blog-cat-${c.toLowerCase()}`}
                  className={`text-xs font-bold tracking-wide uppercase px-3 py-1.5 rounded-full border transition-all ${category === c ? 'border-[#FF2E00] bg-red-50 text-[#FF2E00]' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="text-center py-20"><p className="text-gray-400">Loading...</p></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 mb-2">No articles found.</p>
              {search && <button onClick={() => setSearch('')} className="text-sm text-[#FF2E00] font-bold">Clear search</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => {
                const date = post.created_at ? new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                return (
                  <Link key={post.id || i} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
                    className="bg-white border group overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col" style={{ borderColor: C.border }}>
                    {/* Cover image or color bar */}
                    {post.cover_image ? (
                      <div className="h-40 overflow-hidden">
                        <img src={post.cover_image.startsWith('http') ? post.cover_image : `${API}/api/media/${post.cover_image}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="h-2" style={{ background: C.accent }} />
                    )}
                    <div className="p-5 flex flex-col flex-1">
                      {post.category && (
                        <span className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: C.accent }}>{post.category}</span>
                      )}
                      <h2 className="text-base font-bold leading-tight mb-2 group-hover:text-[#FF2E00] transition-colors" style={{ color: C.dark }}>{post.title}</h2>
                      <p className="text-sm leading-relaxed mb-4 flex-1" style={{ color: C.muted }}>{post.excerpt || ''}</p>
                      <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: C.border }}>
                        <span className="text-xs text-gray-400">{date}</span>
                        <span className="text-xs font-bold flex items-center gap-1 group-hover:text-[#FF2E00] transition-colors" style={{ color: C.muted }}>Read <ChevronRight size={12} /></span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 text-center" style={{ background: C.dark }}>
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl tracking-tighter uppercase mb-3" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>READY TO RUN YOUR GYM LIKE A MACHINE?</h2>
          <p className="text-sm text-gray-400 mb-5">HuscleOS gives you the systems behind the content you just read.</p>
          <Link to="/#quiz" className="inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase px-8 py-3.5 hover:opacity-90" style={{ background: C.accent, color: '#fff' }}>BUILD MY DEMO <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t text-center" style={{ borderColor: C.border }}>
        <div className="flex items-center justify-center gap-6">
          <Link to="/" className="text-xs text-gray-400 hover:text-gray-600">Home</Link>
          <Link to="/merch" className="text-xs text-gray-400 hover:text-gray-600">Merch</Link>
          <Link to="/investors" className="text-xs text-gray-400 hover:text-gray-600">Investors</Link>
        </div>
        <p className="text-xs text-gray-300 mt-3">HuscleOS — The gym operating system.</p>
      </footer>
    </div>
  );
}

/* ── Blog Post Page ────────────────────────────────────────── */
export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/blog/post/${slug}`).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setPost(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAFA' }}><p className="text-gray-400">Loading...</p></div>;
  if (!post) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <div className="text-center"><h1 className="text-2xl font-bold mb-3" style={{ color: C.dark }}>Article not found</h1>
        <Link to="/blog" className="text-sm font-bold" style={{ color: C.accent }}>Back to Blog</Link></div>
    </div>
  );

  const date = post.created_at ? new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif', background: '#FAFAFA' }}>
      <Helmet>
        <title>{post.seo_title || `${post.title} | HuscleOS Blog`}</title>
        <meta name="description" content={post.seo_description || post.excerpt || 'Read this HuscleOS article for independent gym operators.'} />
        <meta name="keywords" content={(post.tags || []).concat(['gym software', 'gym CRM', 'gym marketing']).join(', ')} />
        <link rel="canonical" href={`https://huscleos.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || ''} />
        <meta property="og:url" content={`https://huscleos.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <meta property="article:author" content={post.author || 'HuscleOS'} />
        {post.created_at && <meta property="article:published_time" content={post.created_at} />}
        {post.category && <meta property="article:section" content={post.category} />}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt || post.seo_description || '',
          image: post.cover_image || undefined,
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { '@type': 'Organization', name: post.author || 'HuscleOS' },
          publisher: { '@type': 'Organization', name: 'HuscleOS', logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_f0e6860d-0e81-45b1-9e0b-f7bb6a04df72/artifacts/uf08gcdo_20260313_151045_0000.png' } },
          mainEntityOfPage: `https://huscleos.com/blog/${post.slug}`,
          articleSection: post.category,
        })}</script>
      </Helmet>
      <HuscleosHeader variant="solid" />
      <div className="h-16" />
      <div className="border-b bg-white" style={{ borderColor: C.border }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center">
          <Link to="/blog" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="blogpost-back"><ArrowLeft size={16} /> Back to Blog</Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {post.category && <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent }}>{post.category}</p>}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>{post.title}</h1>
          <div className="flex items-center gap-4 mb-8 pb-6 border-b" style={{ borderColor: C.border }}>
            {date && <span className="flex items-center gap-1.5 text-xs text-gray-400"><Calendar size={13} /> {date}</span>}
            {post.author && <span className="text-xs text-gray-400">By {post.author}</span>}
            {post.tags?.length > 0 && (
              <div className="flex items-center gap-1.5">{post.tags.map((t, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{t}</span>)}</div>
            )}
          </div>
          {post.cover_image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img src={post.cover_image.startsWith('http') ? post.cover_image : `${API}/api/media/${post.cover_image}`} alt="" className="w-full h-auto" />
            </div>
          )}
          <div className="prose prose-gray max-w-none" style={{ color: C.text, lineHeight: 1.8, fontSize: '15px' }} dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
      </article>

      {/* CTA */}
      <section className="py-12 text-center" style={{ background: C.dark }}>
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl tracking-tighter uppercase mb-3" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>WANT THE SYSTEM BEHIND THIS?</h2>
          <p className="text-sm text-gray-400 mb-5">HuscleOS turns these strategies into automated workflows for your gym.</p>
          <Link to="/#quiz" className="inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase px-8 py-3.5 hover:opacity-90" style={{ background: C.accent, color: '#fff' }}>BUILD MY DEMO <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t text-center" style={{ borderColor: C.border }}>
        <Link to="/blog" className="text-xs text-gray-400 hover:text-gray-600">Back to all articles</Link>
      </footer>
    </div>
  );
}
